import type React from "react";
import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { roleInfo } from "simplydo/core/permissions";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import {
  List,
  ListContent,
  ListHeader,
  ListItem,
} from "simplydo/react/ui/list";
import { Popup } from "simplydo/react/ui/popup";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import util from "utils/utils";

import type { Permission, PermissionGroup } from "./RoleEditor";
import { getRoleIcon } from "./roleIcons";
import type { IRole } from "./types";

export type IRolePermissionConstant = {
  key: string;
  text: string;
  title: string;
};

type GroupedPermissions = { group: PermissionGroup; permissions: Permission[] };

const slicePermissionGroups = (
  groupedPermissions: GroupedPermissions[],
  start: number,
  end: number,
) => {
  let offset = 0;
  return groupedPermissions
    .map(({ group, permissions }) => {
      const groupOffset = offset;
      offset += permissions.length;
      return {
        group,
        permissions: permissions.slice(
          Math.max(start - groupOffset, 0),
          Math.max(end - groupOffset, 0),
        ),
      };
    })
    .filter(({ permissions }) => permissions.length > 0);
};

type RoleTableProps = {
  roles: IRole[];
  permissionGroups: PermissionGroup[];
  getPermissionActions?: (
    roleId: string,
    permission: IRolePermissionConstant,
  ) => React.ReactNode;
  getRoleActions?: (role: IRole) => React.ReactNode;
};

const RoleTable = ({
  roles,
  permissionGroups,
  getPermissionActions,
  getRoleActions,
}: RoleTableProps) => {
  const { t } = useTranslation();
  const getPermissionsByGroup = (permissions: string[]): GroupedPermissions[] =>
    permissionGroups
      .map((group) => ({
        group,
        permissions: group.permissions
          .flatMap((category) => category.permissions)
          .filter((permission) => permissions.includes(permission.key)),
      }))
      .filter(({ permissions }) => permissions.length > 0);

  const showGroupHeaders = permissionGroups.length > 1;

  const renderPermissionGroups = (
    groupedPermissions: GroupedPermissions[],
    renderPermission: (permission: Permission) => React.ReactNode,
  ) =>
    groupedPermissions.map(({ group, permissions }, index) => (
      <div key={group.id} style={{ marginTop: index ? 8 : 0 }}>
        {showGroupHeaders ? (
          <ListHeader
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              whiteSpace: "nowrap",
            }}
          >
            <Icon icon={roleInfo[group.type].icon} size="small" />
            {group.title}
          </ListHeader>
        ) : null}
        <List compact variant="bulleted">
          {permissions.map(renderPermission)}
        </List>
      </div>
    ));

  const permissionsPerRowLimit = 7;
  const hasRoleWithMoreThanRowLimit = useMemo(
    () =>
      !!roles.find((role) => role.permissions.length > permissionsPerRowLimit),
    [roles],
  );

  return (
    <div style={{ maxWidth: "100%", overflow: "auto" }}>
      <Table unstackable compact>
        <TableHeader>
          <TableRow>
            <TableHeaderCell></TableHeaderCell>
            <TableHeaderCell>Role</TableHeaderCell>
            <TableHeaderCell>Permissions</TableHeaderCell>
            {hasRoleWithMoreThanRowLimit ? (
              <TableHeaderCell></TableHeaderCell>
            ) : null}
            <TableHeaderCell>Users</TableHeaderCell>
            <TableHeaderCell></TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {roles.map((role) => {
            const roleActions = getRoleActions ? getRoleActions(role) : null;
            const groupedPermissions = getPermissionsByGroup(role.permissions);
            const permissionCount = groupedPermissions.reduce(
              (count, { permissions }) => count + permissions.length,
              0,
            );

            const hasWrappingPermissions =
              permissionCount > permissionsPerRowLimit * 2;
            const wrappedPermissionCount =
              permissionCount - permissionsPerRowLimit * 2;

            const renderPermission = (permission: Permission) => {
              const permissionActions = getPermissionActions
                ? getPermissionActions(role._id, permission)
                : null;
              return (
                <ListItem key={permission.key} style={{ whiteSpace: "nowrap" }}>
                  {permissionActions ? (
                    <ListContent floated="right">
                      {permissionActions}
                    </ListContent>
                  ) : null}
                  <Popup
                    trigger={<ListContent>{t(permission.title)}</ListContent>}
                  >
                    {t(permission.text)}
                  </Popup>
                </ListItem>
              );
            };

            return (
              <TableRow key={role._id}>
                <TableCell collapsing>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 5,
                      alignItems: "center",
                    }}
                  >
                    {role.icon ? (
                      <Popup
                        trigger={
                          <Icon
                            icon={getRoleIcon(role.icon.name)}
                            size="large"
                            color={role.icon.colour}
                          />
                        }
                      >{`Custom icon for this role. This icon displays next to users' names in ${util.appName()} if they have this role. You can change this by clicking "Manage".`}</Popup>
                    ) : null}
                    {role.isDefaultRole ? (
                      <Popup
                        trigger={
                          <Label size="small" color="blue">
                            Default role
                          </Label>
                        }
                      >
                        This role is automatically assigned to all users by
                        default.
                      </Popup>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ flex: 1 }}>{role.name}</div>
                    {role.organisations?.length ? (
                      <p>
                        <span>
                          Organisation specific functionality in this role is
                          limited to the following organisations:
                        </span>
                        <List compact variant="bulleted">
                          {role.organisations.map((org) => (
                            <ListItem key={org._id}>
                              <ListContent>
                                <ListHeader>- {org.name}</ListHeader>
                              </ListContent>
                            </ListItem>
                          ))}
                        </List>
                      </p>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell verticalAlign="top">
                  {renderPermissionGroups(
                    slicePermissionGroups(
                      groupedPermissions,
                      0,
                      permissionsPerRowLimit,
                    ),
                    renderPermission,
                  )}
                </TableCell>
                {hasRoleWithMoreThanRowLimit ? (
                  <TableCell verticalAlign="top">
                    {renderPermissionGroups(
                      slicePermissionGroups(
                        groupedPermissions,
                        permissionsPerRowLimit,
                        hasWrappingPermissions
                          ? permissionsPerRowLimit * 2 - 1
                          : permissionsPerRowLimit * 2,
                      ),
                      renderPermission,
                    )}
                    {hasWrappingPermissions ? (
                      <List compact>
                        <Popup
                          hoverable
                          trigger={
                            <ListItem>
                              <ListContent>
                                + {wrappedPermissionCount} more
                              </ListContent>
                            </ListItem>
                          }
                        >
                          <div style={{ maxHeight: 250, overflow: "auto" }}>
                            {renderPermissionGroups(
                              groupedPermissions,
                              (permission) => (
                                <ListItem key={permission.key}>
                                  <ListContent>
                                    {t(permission.title)}
                                  </ListContent>
                                </ListItem>
                              ),
                            )}
                          </div>
                        </Popup>
                      </List>
                    ) : null}
                  </TableCell>
                ) : null}
                <TableCell collapsing>
                  {role.isDefaultRole ? "All" : (role.userCount ?? 0)}
                </TableCell>
                <TableCell collapsing>
                  {roleActions ? roleActions : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default RoleTable;
